import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { itinerariesService } from '../../services';
import EditItineraryModal from './EditItineraryModal';
import travelieTheme from '../../styles/travelieTheme';
import Modal from '../common/Modal';
import './ItinerariesList.css';
import './ItinerariesList-Mobile.css';
import './ItinerariesList-Responsive.css';
import SecurePaymentDetailsModal from '../Payments/SecurePaymentDetailsModal';
import { formatTravelDateDisplay } from '../../utils/dateUtils';

// Cache-busting comment to force new build hash

const ItinerariesList = () => {
  const { navigate } = useAuth();
  const [itineraries, setItineraries] = useState([]);
  const [completedItineraries, setCompletedItineraries] = useState([]);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'completed'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [showSendUpdateModal, setShowSendUpdateModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [sendingUpdate, setSendingUpdate] = useState(false);
  const [showSecurePaymentModal, setShowSecurePaymentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItineraryForDeletion, setSelectedItineraryForDeletion] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [initialTab, setInitialTab] = useState('details');

  useEffect(() => {
    fetchItineraries();
  }, []);

  const fetchItineraries = async () => {
    try {
      setLoading(true);
      
      // Fetch both active and completed itineraries
      const [activeData, completedData] = await Promise.all([
        itinerariesService.getAll(),
        itinerariesService.getCompleted()
      ]);
      
      // Ensure data is an array
      const activeArray = Array.isArray(activeData) ? activeData : activeData.data || [];
      const completedArray = Array.isArray(completedData) ? completedData : completedData.data || [];
      
      setItineraries(activeArray);
      setCompletedItineraries(completedArray);
    } catch (error) {
      console.error('Error fetching itineraries:', error);
      setError('Failed to load itineraries');
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (itinerary) => {
    if (!itinerary.travel_start_date || !itinerary.travel_end_date) {
      return 'upcoming';
    }
    
    const now = new Date();
    const startDate = new Date(itinerary.travel_start_date);
    const endDate = new Date(itinerary.travel_end_date);
    
    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'completed';
    return 'active';
  };


  const getClientName = (itinerary) => {
    // Try multiple ways to get the client name
    if (itinerary.first_name && itinerary.last_name) {
      return `${itinerary.first_name} ${itinerary.last_name}`;
    }
    
    if (itinerary.client_name) {
      return itinerary.client_name;
    }
    
    // Try to parse from lead_traveler if it exists
    if (itinerary.lead_traveler) {
      try {
        const leadTraveler = typeof itinerary.lead_traveler === 'string' 
          ? JSON.parse(itinerary.lead_traveler) 
          : itinerary.lead_traveler;
        
        const firstName = leadTraveler.firstName || leadTraveler.first_name || '';
        const lastName = leadTraveler.lastName || leadTraveler.last_name || '';
        
        if (firstName || lastName) {
          return `${firstName} ${lastName}`.trim();
        }
      } catch (e) {
        console.error('Error parsing lead_traveler:', e);
      }
    }
    
    return 'Unknown Client';
  };

  // Button style helpers
  const getButtonStyle = (type = 'primary') => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.md}`,
      border: 'none',
      borderRadius: travelieTheme.components.button.primary.borderRadius,
      fontSize: travelieTheme.typography.fontSize.xs,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      cursor: 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      whiteSpace: 'nowrap'
    };

    const styles = {
      primary: {
        ...baseStyle,
        background: travelieTheme.colors.primary.main,
        color: travelieTheme.colors.text.white
      },
      secondary: {
        ...baseStyle,
        background: '#6B7280',
        color: travelieTheme.colors.text.white
      },
      success: {
        ...baseStyle,
        background: travelieTheme.colors.status.success,
        color: travelieTheme.colors.text.white
      },
      warning: {
        ...baseStyle,
        background: travelieTheme.colors.status.warning,
        color: travelieTheme.colors.text.white
      },
      danger: {
        ...baseStyle,
        background: travelieTheme.colors.status.error,
        color: travelieTheme.colors.text.white
      },
      info: {
        ...baseStyle,
        background: '#3B82F6',
        color: travelieTheme.colors.text.white
      }
    };

    return styles[type] || styles.primary;
  };

  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
      borderRadius: '12px',
      fontSize: travelieTheme.typography.fontSize.xs,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      whiteSpace: 'nowrap'
    };

    const styles = {
      upcoming: {
        ...baseStyle,
        background: '#FEF3C7',
        color: '#92400E'
      },
      active: {
        ...baseStyle,
        background: travelieTheme.colors.status.successLight,
        color: '#065F46'
      },
      completed: {
        ...baseStyle,
        background: '#E0E7FF',
        color: '#3730A3'
      }
    };

    return styles[status] || styles.upcoming;
  };

  const handleEditItinerary = async (itinerary, tab = 'details') => {
    // Fetch full itinerary details
    try {
      const fullItinerary = await itinerariesService.getById(itinerary.id);
      setSelectedItinerary(fullItinerary);
      setInitialTab(tab);
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching itinerary details:', error);
      alert('Failed to load itinerary details');
    }
  };

  const handleRecordPayment = async (itinerary) => {
    // Open edit modal directly to supplier payments tab
    await handleEditItinerary(itinerary, 'supplier-payments');
  };



  const handleSendUpdate = (itinerary) => {
    setSelectedItinerary(itinerary);
    setUpdateMessage('');
    setShowSendUpdateModal(true);
  };

  const handleSendUpdateSubmit = async () => {
    if (!updateMessage.trim()) {
      alert('Please enter a message about the update');
      return;
    }

    setSendingUpdate(true);
    try {
      // Send update email to client (this also adds the version entry)
      await itinerariesService.sendUpdate(selectedItinerary.id, {
        message: updateMessage
      });

      alert('Update sent to client successfully!');
      setShowSendUpdateModal(false);
      setUpdateMessage('');
      fetchItineraries(); // Refresh the list
    } catch (error) {
      console.error('Error sending update:', error);
      alert('Failed to send update. Please try again.');
    } finally {
      setSendingUpdate(false);
    }
  };

  const handleSendPortalLink = async (itinerary) => {
    if (!itinerary.quote_id) {
      alert('This itinerary is not linked to a quote. Portal links are only available for quote-based itineraries.');
      return;
    }

    if (!confirm(`Send trip portal link to ${getClientName(itinerary)}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/public/portal/send/${itinerary.quote_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send portal link');
      }

      const result = await response.json();
      alert(`Portal link sent successfully to ${getClientName(itinerary)}!`);
    } catch (error) {
      console.error('Error sending portal link:', error);
      alert(`Failed to send portal link: ${error.message}`);
    }
  };

  const handleViewPayments = (itinerary) => {
    setSelectedItinerary(itinerary);
    setShowSecurePaymentModal(true);
  };

  const handleDeleteClick = (itinerary) => {
    setSelectedItineraryForDeletion(itinerary);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItineraryForDeletion) return;
    
    // Require typing "DELETE" for confirmation
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type "DELETE" to confirm deletion');
      return;
    }

    try {
      setIsDeleting(true);
      await itinerariesService.delete(selectedItineraryForDeletion.id);
      
      // Remove from both active and completed lists
      setItineraries(itineraries.filter(i => i.id !== selectedItineraryForDeletion.id));
      setCompletedItineraries(completedItineraries.filter(i => i.id !== selectedItineraryForDeletion.id));
      
      setShowDeleteModal(false);
      setSelectedItineraryForDeletion(null);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Error deleting itinerary:', error);
      alert('Failed to delete itinerary. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleArchive = async (itinerary) => {
    if (!confirm(`Archive "${itinerary.title}"? This will move it to the Completed Travel tab.`)) {
      return;
    }

    try {
      await itinerariesService.archive(itinerary.id);
      
      // Remove from active list and add to completed list
      setItineraries(itineraries.filter(i => i.id !== itinerary.id));
      setCompletedItineraries([itinerary, ...completedItineraries]);
      
      alert('Itinerary archived successfully!');
    } catch (error) {
      console.error('Error archiving itinerary:', error);
      alert('Failed to archive itinerary. Please try again.');
    }
  };

  const handleUnarchive = async (itinerary) => {
    if (!confirm(`Unarchive "${itinerary.title}"? This will move it back to Active Bookings.`)) {
      return;
    }

    try {
      await itinerariesService.unarchive(itinerary.id);
      
      // Remove from completed list and add to active list
      setCompletedItineraries(completedItineraries.filter(i => i.id !== itinerary.id));
      setItineraries([itinerary, ...itineraries]);
      
      alert('Itinerary unarchived successfully!');
    } catch (error) {
      console.error('Error unarchiving itinerary:', error);
      alert('Failed to unarchive itinerary. Please try again.');
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedItineraryForDeletion(null);
    setDeleteConfirmText('');
  };

  // Get current itineraries based on active tab (respects is_archived flag)
  const currentItineraries = activeTab === 'active' ? itineraries : completedItineraries;

  // Filter itineraries based on search and status filter
  const filteredItineraries = currentItineraries.filter(itinerary => {
    const clientName = getClientName(itinerary);
    const matchesSearch = 
      itinerary.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const status = getStatus(itinerary);
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Group itineraries by date-based status for display within current tab
  // This shows status sections (upcoming/active/completed) based on travel dates
  // but only within the current tab's dataset (active bookings or completed travel)
  const groupedItineraries = {
    upcoming: filteredItineraries.filter(i => getStatus(i) === 'upcoming'),
    active: filteredItineraries.filter(i => getStatus(i) === 'active'),
    completed: filteredItineraries.filter(i => getStatus(i) === 'completed')
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        fontSize: travelieTheme.typography.fontSize.lg,
        color: travelieTheme.colors.text.secondary
      }}>
        Loading itineraries...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: '#FEF2F2',
        border: '1px solid #FECACA',
        color: '#DC2626',
        padding: `${travelieTheme.spacing.lg} ${travelieTheme.spacing.xl}`,
        borderRadius: travelieTheme.components.card.borderRadius,
        margin: travelieTheme.spacing['2xl'],
        fontSize: travelieTheme.typography.fontSize.sm
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ padding: travelieTheme.spacing['2xl'] }}>
      {/* Header Section */}
      <div className="itineraries-header-container" style={{
        marginBottom: travelieTheme.spacing['2xl']
      }}>
        <div>
          <h1 style={{
            fontSize: travelieTheme.typography.fontSize['2xl'],
            fontWeight: travelieTheme.typography.fontWeight.bold,
            color: travelieTheme.colors.text.primary,
            margin: 0,
            marginBottom: travelieTheme.spacing.xs
          }}>Travel Itineraries</h1>
          <p style={{
            fontSize: travelieTheme.typography.fontSize.sm,
            color: travelieTheme.colors.text.secondary,
            margin: 0
          }}>Manage your clients' travel bookings and itineraries</p>
        </div>
        <div className="header-stats-container">
          <div style={{
            background: travelieTheme.colors.primary.light,
            padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
            borderRadius: '8px',
            textAlign: 'center',
            minWidth: '120px'
          }}>
            <div style={{
              fontSize: travelieTheme.typography.fontSize.xs,
              color: travelieTheme.colors.text.secondary,
              marginBottom: travelieTheme.spacing.xs
            }}>Active Bookings</div>
            <div style={{
              fontSize: travelieTheme.typography.fontSize.xl,
              fontWeight: travelieTheme.typography.fontWeight.bold,
              color: travelieTheme.colors.primary.main
            }}>{itineraries.length}</div>
          </div>
          <div style={{
            background: '#E0E7FF',
            padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
            borderRadius: '8px',
            textAlign: 'center',
            minWidth: '120px'
          }}>
            <div style={{
              fontSize: travelieTheme.typography.fontSize.xs,
              color: travelieTheme.colors.text.secondary,
              marginBottom: travelieTheme.spacing.xs
            }}>Completed Travel</div>
            <div style={{
              fontSize: travelieTheme.typography.fontSize.xl,
              fontWeight: travelieTheme.typography.fontWeight.bold,
              color: '#3730A3'
            }}>{completedItineraries.length}</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="itineraries-tabs-container" style={{
        marginBottom: travelieTheme.spacing['2xl']
      }}>
        <button 
          onClick={() => setActiveTab('active')}
          style={{
            padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.lg}`,
            background: activeTab === 'active' ? travelieTheme.colors.primary.main : 'transparent',
            color: activeTab === 'active' ? travelieTheme.colors.text.white : travelieTheme.colors.text.secondary,
            border: 'none',
            borderBottom: activeTab === 'active' ? `3px solid ${travelieTheme.colors.primary.main}` : '3px solid transparent',
            cursor: 'pointer',
            fontSize: travelieTheme.typography.fontSize.sm,
            fontWeight: activeTab === 'active' ? travelieTheme.typography.fontWeight.semibold : travelieTheme.typography.fontWeight.normal,
            transition: `all ${travelieTheme.transitions.fast}`,
            borderRadius: activeTab === 'active' ? '4px 4px 0 0' : '0'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'active') {
              e.currentTarget.style.background = travelieTheme.colors.neutral.background;
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'active') {
              e.currentTarget.style.background = 'transparent';
            }
          }}
        >
          Active Bookings ({itineraries.length})
        </button>
        <button 
          onClick={() => setActiveTab('completed')}
          style={{
            padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.lg}`,
            background: activeTab === 'completed' ? travelieTheme.colors.primary.main : 'transparent',
            color: activeTab === 'completed' ? travelieTheme.colors.text.white : travelieTheme.colors.text.secondary,
            border: 'none',
            borderBottom: activeTab === 'completed' ? `3px solid ${travelieTheme.colors.primary.main}` : '3px solid transparent',
            cursor: 'pointer',
            fontSize: travelieTheme.typography.fontSize.sm,
            fontWeight: activeTab === 'completed' ? travelieTheme.typography.fontWeight.semibold : travelieTheme.typography.fontWeight.normal,
            transition: `all ${travelieTheme.transitions.fast}`,
            borderRadius: activeTab === 'completed' ? '4px 4px 0 0' : '0'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'completed') {
              e.currentTarget.style.background = travelieTheme.colors.neutral.background;
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'completed') {
              e.currentTarget.style.background = 'transparent';
            }
          }}
        >
          Completed Travel ({completedItineraries.length})
        </button>
      </div>

      {/* Filters */}
      <div className="itineraries-filters-container" style={{
        marginBottom: travelieTheme.spacing['2xl']
      }}>
        <input
          type="text"
          placeholder="Search by title or client name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
            border: `1px solid ${travelieTheme.colors.neutral.border}`,
            borderRadius: travelieTheme.components.input.borderRadius,
            fontSize: travelieTheme.typography.fontSize.sm,
            outline: 'none',
            transition: `border-color ${travelieTheme.transitions.fast}`
          }}
          onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
          onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
        />
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
            border: `1px solid ${travelieTheme.colors.neutral.border}`,
            borderRadius: travelieTheme.components.input.borderRadius,
            fontSize: travelieTheme.typography.fontSize.sm,
            outline: 'none',
            cursor: 'pointer',
            minWidth: '180px'
          }}
        >
          <option value="all">All Status</option>
          <option value="upcoming">Upcoming</option>
          <option value="active">Currently Traveling</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Upcoming Travel */}
      {groupedItineraries.upcoming.length > 0 && (
        <div style={{ marginBottom: travelieTheme.spacing['3xl'] }}>
          <h2 style={{
            fontSize: travelieTheme.typography.fontSize.xl,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: travelieTheme.spacing.sm
          }}>
            <span>📅</span>
            Upcoming Travel
          </h2>
          <div className="itinerary-grid-container">
            {groupedItineraries.upcoming.map(itinerary => (
              <div key={itinerary.id} className="itinerary-card-container" style={{
                background: travelieTheme.colors.neutral.card,
                borderRadius: travelieTheme.components.card.borderRadius,
                padding: travelieTheme.spacing.lg,
                boxShadow: travelieTheme.components.card.shadow,
                border: `1px solid ${travelieTheme.colors.neutral.border}`,
                transition: `box-shadow ${travelieTheme.transitions.fast}`
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = travelieTheme.components.card.shadow}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: travelieTheme.spacing.md,
                  gap: travelieTheme.spacing.sm
                }}>
                  <h3 style={{
                    fontSize: travelieTheme.typography.fontSize.lg,
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: travelieTheme.colors.text.primary,
                    margin: 0,
                    flex: 1
                  }}>{itinerary.title}</h3>
                  <span style={getStatusBadgeStyle('upcoming')}>Upcoming</span>
                </div>
                <div className="itinerary-details-container" style={{
                  marginBottom: travelieTheme.spacing.md
                }}>
                  <p style={{
                    margin: `${travelieTheme.spacing.xs} 0`,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>Client:</strong> {getClientName(itinerary)}
                  </p>
                  <p style={{
                    margin: `${travelieTheme.spacing.xs} 0`,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>Travel Dates:</strong> {' '}
                    {formatTravelDateDisplay(itinerary.travel_start_date)} - 
                    {formatTravelDateDisplay(itinerary.travel_end_date)}
                  </p>
                  <p style={{
                    margin: `${travelieTheme.spacing.xs} 0`,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>Total:</strong> ${parseFloat(itinerary.total_amount || 0).toFixed(2)}
                  </p>
                  {itinerary.payment_status && (
                    <p style={{
                      margin: `${travelieTheme.spacing.xs} 0`,
                      fontSize: travelieTheme.typography.fontSize.sm,
                      color: travelieTheme.colors.text.secondary
                    }}>
                      <strong style={{ color: travelieTheme.colors.text.primary }}>Payment:</strong> {itinerary.payment_status.replace('_', ' ')}
                    </p>
                  )}
                  {itinerary.hasPayments && (
                    <button 
                      onClick={() => handleViewPayments(itinerary)}
                      style={{
                        ...getButtonStyle('secondary'),
                        marginTop: travelieTheme.spacing.sm
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      View Payments ({itinerary.payment_count})
                    </button>
                  )}
                </div>
                <div className="itinerary-actions-container" style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: travelieTheme.spacing.xs
                }}>
                  <button
                    onClick={() => handleEditItinerary(itinerary)}
                    style={getButtonStyle('primary')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Edit Details
                  </button>
                  <button 
                    onClick={() => handleSendUpdate(itinerary)}
                    style={getButtonStyle('secondary')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Send Update
                  </button>
                  {itinerary.quote_id && (
                    <button 
                      onClick={() => handleSendPortalLink(itinerary)}
                      title="Send trip portal link to client"
                      style={getButtonStyle('info')}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      🔗 Send Portal Link
                    </button>
                  )}
                  <button
                    onClick={() => handleRecordPayment(itinerary)}
                    style={getButtonStyle('success')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Record Payment
                  </button>
                  <button
                    onClick={() => handleArchive(itinerary)}
                    title="Move to Completed Travel tab"
                    style={getButtonStyle('warning')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    📦 Archive
                  </button>
                  <button
                    onClick={() => handleDeleteClick(itinerary)}
                    style={getButtonStyle('danger')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Currently Traveling */}
      {groupedItineraries.active.length > 0 && (
        <div style={{ marginBottom: travelieTheme.spacing['3xl'] }}>
          <h2 style={{
            fontSize: travelieTheme.typography.fontSize.xl,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: travelieTheme.spacing.sm
          }}>
            <span>✈️</span>
            Currently Traveling
          </h2>
          <div className="itinerary-grid-container">
            {groupedItineraries.active.map(itinerary => (
              <div key={itinerary.id} className="itinerary-card-container" style={{
                background: travelieTheme.colors.neutral.card,
                borderRadius: travelieTheme.components.card.borderRadius,
                padding: travelieTheme.spacing.lg,
                boxShadow: travelieTheme.components.card.shadow,
                border: `1px solid ${travelieTheme.colors.neutral.border}`,
                transition: `box-shadow ${travelieTheme.transitions.fast}`
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = travelieTheme.components.card.shadow}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: travelieTheme.spacing.md,
                  gap: travelieTheme.spacing.sm
                }}>
                  <h3 style={{
                    fontSize: travelieTheme.typography.fontSize.lg,
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: travelieTheme.colors.text.primary,
                    margin: 0,
                    flex: 1
                  }}>{itinerary.title}</h3>
                  <span style={getStatusBadgeStyle('active')}>Active</span>
                </div>
                <div className="itinerary-details-container" style={{
                  marginBottom: travelieTheme.spacing.md
                }}>
                  <p style={{
                    margin: `${travelieTheme.spacing.xs} 0`,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>Client:</strong> {getClientName(itinerary)}
                  </p>
                  <p style={{
                    margin: `${travelieTheme.spacing.xs} 0`,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>Travel Dates:</strong> {' '}
                    {formatTravelDateDisplay(itinerary.travel_start_date)} - 
                    {formatTravelDateDisplay(itinerary.travel_end_date)}
                  </p>
                  <p style={{
                    margin: `${travelieTheme.spacing.xs} 0`,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>Total:</strong> ${parseFloat(itinerary.total_amount || 0).toFixed(2)}
                  </p>
                  {itinerary.payment_status && (
                    <p style={{
                      margin: `${travelieTheme.spacing.xs} 0`,
                      fontSize: travelieTheme.typography.fontSize.sm,
                      color: travelieTheme.colors.text.secondary
                    }}>
                      <strong style={{ color: travelieTheme.colors.text.primary }}>Payment:</strong> {itinerary.payment_status.replace('_', ' ')}
                    </p>
                  )}
                  {itinerary.hasPayments && (
                    <button 
                      onClick={() => handleViewPayments(itinerary)}
                      style={{
                        ...getButtonStyle('secondary'),
                        marginTop: travelieTheme.spacing.sm
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      View Payments ({itinerary.payment_count})
                    </button>
                  )}
                </div>
                <div className="itinerary-actions-container" style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: travelieTheme.spacing.xs
                }}>
                  <button
                    onClick={() => handleEditItinerary(itinerary)}
                    style={getButtonStyle('primary')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    View Details
                  </button>
                  {itinerary.quote_id && (
                    <button 
                      onClick={() => handleSendPortalLink(itinerary)}
                      title="Send trip portal link to client"
                      style={getButtonStyle('info')}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      🔗 Send Portal Link
                    </button>
                  )}
                  <button
                    onClick={() => handleRecordPayment(itinerary)}
                    style={getButtonStyle('success')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Record Payment
                  </button>
                  <button
                    onClick={() => handleArchive(itinerary)}
                    title="Move to Completed Travel tab"
                    style={getButtonStyle('warning')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    📦 Archive
                  </button>
                  <button
                    onClick={() => handleDeleteClick(itinerary)}
                    style={getButtonStyle('danger')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Travel (within Active Bookings tab) */}
      {activeTab === 'active' && groupedItineraries.completed.length > 0 && (
        <div style={{ marginBottom: travelieTheme.spacing['3xl'] }}>
          <h2 style={{
            fontSize: travelieTheme.typography.fontSize.xl,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: travelieTheme.spacing.sm
          }}>
            <span>✅</span>
            Completed Travel
          </h2>
          <div className="itinerary-grid-container">
            {groupedItineraries.completed.map(itinerary => (
              <div key={itinerary.id} className="itinerary-card-container" style={{
                background: travelieTheme.colors.neutral.card,
                borderRadius: travelieTheme.components.card.borderRadius,
                padding: travelieTheme.spacing.lg,
                boxShadow: travelieTheme.components.card.shadow,
                border: `1px solid ${travelieTheme.colors.neutral.border}`,
                transition: `box-shadow ${travelieTheme.transitions.fast}`
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = travelieTheme.components.card.shadow}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: travelieTheme.spacing.md,
                  gap: travelieTheme.spacing.sm
                }}>
                  <h3 style={{
                    fontSize: travelieTheme.typography.fontSize.lg,
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: travelieTheme.colors.text.primary,
                    margin: 0,
                    flex: 1
                  }}>{itinerary.title}</h3>
                  <span style={getStatusBadgeStyle('completed')}>Completed</span>
                </div>
                <div className="itinerary-details-container" style={{
                  marginBottom: travelieTheme.spacing.md
                }}>
                  <p style={{
                    margin: `${travelieTheme.spacing.xs} 0`,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>Client:</strong> {getClientName(itinerary)}
                  </p>
                  <p style={{
                    margin: `${travelieTheme.spacing.xs} 0`,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>Travel Dates:</strong> {' '}
                    {formatTravelDateDisplay(itinerary.travel_start_date)} - 
                    {formatTravelDateDisplay(itinerary.travel_end_date)}
                  </p>
                  <p style={{
                    margin: `${travelieTheme.spacing.xs} 0`,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>Total:</strong> ${parseFloat(itinerary.total_amount || 0).toFixed(2)}
                  </p>
                  {itinerary.payment_status && (
                    <p style={{
                      margin: `${travelieTheme.spacing.xs} 0`,
                      fontSize: travelieTheme.typography.fontSize.sm,
                      color: travelieTheme.colors.text.secondary
                    }}>
                      <strong style={{ color: travelieTheme.colors.text.primary }}>Payment:</strong> {itinerary.payment_status.replace('_', ' ')}
                    </p>
                  )}
                  {itinerary.hasPayments && (
                    <button 
                      onClick={() => handleViewPayments(itinerary)}
                      style={{
                        ...getButtonStyle('secondary'),
                        marginTop: travelieTheme.spacing.sm
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      View Payments ({itinerary.payment_count})
                    </button>
                  )}
                </div>
                <div className="itinerary-actions-container" style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: travelieTheme.spacing.xs
                }}>
                  <button
                    onClick={() => handleEditItinerary(itinerary)}
                    style={getButtonStyle('primary')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    View Details
                  </button>
                  {itinerary.quote_id && (
                    <button 
                      onClick={() => handleSendPortalLink(itinerary)}
                      title="Send trip portal link to client"
                      style={getButtonStyle('info')}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      🔗 Send Portal Link
                    </button>
                  )}
                  <button
                    onClick={() => handleRecordPayment(itinerary)}
                    style={getButtonStyle('success')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Record Payment
                  </button>
                  <button
                    onClick={() => handleArchive(itinerary)}
                    title="Move to Completed Travel tab"
                    style={getButtonStyle('warning')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    📦 Archive
                  </button>
                  <button
                    onClick={() => handleDeleteClick(itinerary)}
                    style={getButtonStyle('danger')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Travel Tab */}
      {activeTab === 'completed' && groupedItineraries.completed.length > 0 && (
        <div style={{ marginBottom: travelieTheme.spacing['3xl'] }}>
          <h2 style={{
            fontSize: travelieTheme.typography.fontSize.xl,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: travelieTheme.spacing.sm
          }}>
            <span>✅</span>
            Completed Travel
          </h2>
          <div className="itinerary-grid-container">
            {groupedItineraries.completed.map(itinerary => {
              return (
                <div key={itinerary.id} className="itinerary-card-container" style={{
                  background: travelieTheme.colors.neutral.card,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  padding: travelieTheme.spacing.lg,
                  boxShadow: travelieTheme.components.card.shadow,
                  border: `1px solid ${travelieTheme.colors.neutral.border}`,
                  transition: `box-shadow ${travelieTheme.transitions.fast}`
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = travelieTheme.components.card.shadow}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: travelieTheme.spacing.md,
                    gap: travelieTheme.spacing.sm
                  }}>
                    <h3 style={{
                      fontSize: travelieTheme.typography.fontSize.lg,
                      fontWeight: travelieTheme.typography.fontWeight.semibold,
                      color: travelieTheme.colors.text.primary,
                      margin: 0,
                      flex: 1
                    }}>{itinerary.title}</h3>
                    <span style={getStatusBadgeStyle('completed')}>Completed</span>
                  </div>
                  <div className="itinerary-details-container" style={{
                    marginBottom: travelieTheme.spacing.md
                  }}>
                    <p style={{
                      margin: `${travelieTheme.spacing.xs} 0`,
                      fontSize: travelieTheme.typography.fontSize.sm,
                      color: travelieTheme.colors.text.secondary
                    }}>
                      <strong style={{ color: travelieTheme.colors.text.primary }}>Client:</strong> {getClientName(itinerary)}
                    </p>
                    <p style={{
                      margin: `${travelieTheme.spacing.xs} 0`,
                      fontSize: travelieTheme.typography.fontSize.sm,
                      color: travelieTheme.colors.text.secondary
                    }}>
                      <strong style={{ color: travelieTheme.colors.text.primary }}>Travel Dates:</strong> {' '}
                      {formatTravelDateDisplay(itinerary.travel_start_date)} - 
                      {formatTravelDateDisplay(itinerary.travel_end_date)}
                    </p>
                    <p style={{
                      margin: `${travelieTheme.spacing.xs} 0`,
                      fontSize: travelieTheme.typography.fontSize.sm,
                      color: travelieTheme.colors.text.secondary
                    }}>
                      <strong style={{ color: travelieTheme.colors.text.primary }}>Total:</strong> ${parseFloat(itinerary.total_amount || 0).toFixed(2)}
                    </p>
                    {itinerary.hasPayments && (
                      <button 
                        onClick={() => handleViewPayments(itinerary)}
                        style={{
                          ...getButtonStyle('secondary'),
                          marginTop: travelieTheme.spacing.sm
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        View Payments ({itinerary.payment_count})
                      </button>
                    )}
                  </div>
                  <div className="itinerary-actions-container" style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: travelieTheme.spacing.xs
                  }}>
                    <button
                      onClick={() => handleEditItinerary(itinerary)}
                      style={getButtonStyle('primary')}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      View Details
                    </button>
                    {itinerary.quote_id && (
                      <button 
                        onClick={() => handleSendPortalLink(itinerary)}
                        title="Send trip portal link to client"
                        style={getButtonStyle('info')}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        🔗 Portal Link
                      </button>
                    )}
                    <button
                      onClick={() => handleUnarchive(itinerary)}
                      title="Move back to Active Bookings"
                      style={getButtonStyle('warning')}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      📤 Unarchive
                    </button>
                    <button
                      onClick={() => handleDeleteClick(itinerary)}
                      style={getButtonStyle('danger')}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {filteredItineraries.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: `${travelieTheme.spacing['4xl']} ${travelieTheme.spacing.xl}`,
          color: travelieTheme.colors.text.secondary,
          fontSize: travelieTheme.typography.fontSize.md
        }}>
          <p style={{ margin: 0 }}>No itineraries found.</p>
        </div>
      )}

      {/* Edit Modal */}
      <EditItineraryModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedItinerary(null);
          setInitialTab('details');
        }}
        itinerary={selectedItinerary}
        initialTab={initialTab}
        onUpdate={() => {
          fetchItineraries();
          setShowEditModal(false);
          setSelectedItinerary(null);
          setInitialTab('details');
        }}
      />



      {/* Secure Payment Details Modal */}
      <SecurePaymentDetailsModal
        isOpen={showSecurePaymentModal}
        onClose={() => {
          setShowSecurePaymentModal(false);
          setSelectedItinerary(null);
        }}
        itinerary={selectedItinerary}
      />

      {/* Send Update Modal */}
      <Modal
        isOpen={showSendUpdateModal}
        onClose={() => setShowSendUpdateModal(false)}
        title="Send Update to Client"
        maxWidth="500px"
        footer={(
          <>
            <button 
              type="button" 
              onClick={() => setShowSendUpdateModal(false)}
              style={getButtonStyle('secondary')}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Cancel
            </button>
            <button 
              type="button" 
              onClick={handleSendUpdateSubmit}
              disabled={sendingUpdate || !updateMessage.trim()}
              style={{
                ...getButtonStyle('primary'),
                opacity: (sendingUpdate || !updateMessage.trim()) ? '0.5' : '1',
                cursor: (sendingUpdate || !updateMessage.trim()) ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!sendingUpdate && updateMessage.trim()) {
                  e.currentTarget.style.opacity = '0.9';
                }
              }}
              onMouseLeave={(e) => {
                if (!sendingUpdate && updateMessage.trim()) {
                  e.currentTarget.style.opacity = '1';
                }
              }}
            >
              {sendingUpdate ? 'Sending...' : 'Send Update'}
            </button>
          </>
        )}
      >
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.lg,
                lineHeight: 1.5
              }}>
                Describe the changes you've made to the itinerary. This message will be sent to the client.
              </p>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.medium,
                  color: travelieTheme.colors.text.primary,
                  marginBottom: travelieTheme.spacing.xs
                }}>Update Message *</label>
                <textarea
                  value={updateMessage}
                  onChange={(e) => setUpdateMessage(e.target.value)}
                  rows="4"
                  placeholder="e.g., Updated your hotel to a suite with ocean view..."
                  required
                  style={{
                    width: '100%',
                    padding: travelieTheme.spacing.sm,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    border: `1px solid ${travelieTheme.colors.neutral.border}`,
                    borderRadius: travelieTheme.components.card.borderRadius,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    transition: `border-color ${travelieTheme.transitions.fast}`,
                    outline: 'none'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
                  onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
                />
              </div>
      </Modal>

      {/* Enhanced Delete Confirmation Modal */}
      {showDeleteModal && selectedItineraryForDeletion && (
        <Modal
          isOpen
          onClose={closeDeleteModal}
          title="⚠️ Delete Itinerary"
          maxWidth="600px"
          footer={(
            <>
              <button 
                onClick={closeDeleteModal}
                disabled={isDeleting}
                style={{
                  ...getButtonStyle('secondary'),
                  opacity: isDeleting ? '0.5' : '1',
                  cursor: isDeleting ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting) e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting) e.currentTarget.style.opacity = '1';
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                style={{
                  ...getButtonStyle('danger'),
                  opacity: (deleteConfirmText !== 'DELETE' || isDeleting) ? '0.5' : '1',
                  cursor: (deleteConfirmText !== 'DELETE' || isDeleting) ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (deleteConfirmText === 'DELETE' && !isDeleting) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (deleteConfirmText === 'DELETE' && !isDeleting) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete Itinerary Permanently'}
              </button>
            </>
          )}
        >
              <div style={{
                background: '#FEF2F2',
                border: `2px solid ${travelieTheme.colors.status.error}`,
                borderRadius: travelieTheme.components.card.borderRadius,
                padding: travelieTheme.spacing.md,
                marginBottom: travelieTheme.spacing.lg
              }}>
                <p style={{
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.status.error,
                  margin: `0 0 ${travelieTheme.spacing.xs} 0`
                }}><strong>⚠️ WARNING: This action cannot be undone!</strong></p>
                <p style={{
                  fontSize: travelieTheme.typography.fontSize.sm,
                  color: travelieTheme.colors.text.secondary,
                  margin: 0
                }}>You are about to permanently delete this itinerary and all associated data.</p>
              </div>
              
              <div style={{
                background: travelieTheme.colors.neutral.background,
                borderRadius: travelieTheme.components.card.borderRadius,
                padding: travelieTheme.spacing.md,
                marginBottom: travelieTheme.spacing.lg
              }}>
                <h3 style={{
                  fontSize: travelieTheme.typography.fontSize.md,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary,
                  marginBottom: travelieTheme.spacing.sm
                }}>Itinerary to be deleted:</h3>
                <div>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary,
                    margin: `${travelieTheme.spacing.xs} 0`
                  }}><strong style={{ color: travelieTheme.colors.text.primary }}>Title:</strong> {selectedItineraryForDeletion.title}</p>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary,
                    margin: `${travelieTheme.spacing.xs} 0`
                  }}><strong style={{ color: travelieTheme.colors.text.primary }}>Client:</strong> {getClientName(selectedItineraryForDeletion)}</p>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary,
                    margin: `${travelieTheme.spacing.xs} 0`
                  }}><strong style={{ color: travelieTheme.colors.text.primary }}>Travel Dates:</strong> {' '}
                    {formatTravelDateDisplay(selectedItineraryForDeletion.travel_start_date)} - 
                    {formatTravelDateDisplay(selectedItineraryForDeletion.travel_end_date)}
                  </p>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary,
                    margin: `${travelieTheme.spacing.xs} 0`
                  }}><strong style={{ color: travelieTheme.colors.text.primary }}>Total Amount:</strong> ${(parseFloat(selectedItineraryForDeletion.total_amount) || 0).toFixed(2)}</p>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary,
                    margin: `${travelieTheme.spacing.xs} 0`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: travelieTheme.spacing.xs
                  }}><strong style={{ color: travelieTheme.colors.text.primary }}>Status:</strong> <span style={getStatusBadgeStyle(getStatus(selectedItineraryForDeletion))}>{getStatus(selectedItineraryForDeletion)}</span></p>
                </div>
              </div>

              {/* Status-based warnings */}
              {getStatus(selectedItineraryForDeletion) === 'active' && (
                <div style={{
                  background: '#FEF2F2',
                  border: `2px solid ${travelieTheme.colors.status.error}`,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  padding: travelieTheme.spacing.md,
                  marginBottom: travelieTheme.spacing.md
                }}>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: travelieTheme.colors.status.error,
                    margin: `0 0 ${travelieTheme.spacing.xs} 0`
                  }}><strong>🚨 CRITICAL: This client is currently traveling!</strong></p>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary,
                    margin: 0
                  }}>Deleting this itinerary may disrupt their active trip and cause confusion.</p>
                </div>
              )}
              
              {getStatus(selectedItineraryForDeletion) === 'upcoming' && (
                <div style={{
                  background: '#FEF3C7',
                  border: `2px solid ${travelieTheme.colors.status.warning}`,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  padding: travelieTheme.spacing.md,
                  marginBottom: travelieTheme.spacing.md
                }}>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: '#92400E',
                    margin: `0 0 ${travelieTheme.spacing.xs} 0`
                  }}><strong>⚠️ This is an upcoming trip.</strong></p>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary,
                    margin: 0
                  }}>Deleting this itinerary may affect the client's travel plans.</p>
                </div>
              )}

              {selectedItineraryForDeletion.hasPayments && (
                <div style={{
                  background: '#FEF2F2',
                  border: `2px solid ${travelieTheme.colors.status.error}`,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  padding: travelieTheme.spacing.md,
                  marginBottom: travelieTheme.spacing.md
                }}>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: travelieTheme.colors.status.error,
                    margin: `0 0 ${travelieTheme.spacing.xs} 0`
                  }}><strong>🚨 This itinerary has {selectedItineraryForDeletion.payment_count} payment(s) associated with it!</strong></p>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary,
                    margin: 0
                  }}>Deleting this itinerary may affect payment records and financial reporting.</p>
                </div>
              )}

              <div style={{
                marginTop: travelieTheme.spacing.lg
              }}>
                <p style={{
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary,
                  marginBottom: travelieTheme.spacing.sm
                }}><strong>To confirm deletion, type "DELETE" below:</strong></p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  disabled={isDeleting}
                  style={{
                    width: '100%',
                    padding: travelieTheme.spacing.sm,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    border: `2px solid ${travelieTheme.colors.neutral.border}`,
                    borderRadius: travelieTheme.components.card.borderRadius,
                    fontFamily: 'inherit',
                    transition: `border-color ${travelieTheme.transitions.fast}`,
                    outline: 'none',
                    opacity: isDeleting ? '0.5' : '1'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.status.error}
                  onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
                />
              </div>
        </Modal>
      )}
    </div>
  );
};

export default ItinerariesList;
